import type { TranslationType } from './en';

export const pt: TranslationType = {
  days: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
  months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  pageTitle: 'DOMINION | Perfil de Risco',
  title: 'Relatório de Perfil de Risco',
  profileTitle: 'O Seu Perfil de Risco:',
  allocationTitle: 'Distribuição Recomendada de Ativos',
  recommendationsTitle: 'Recomendações de Investimento:',
  recommendationText: 'da sua carteira',
  footer: 'Este é um documento gerado pelo sistema. Apenas para uso interno.',
  website: 'www.dominion-cs.com',
  signature: {
    title: 'Assinatura e Aceitação',
    clientName: 'Nome do Cliente:',
    signature: 'Assinatura:',
    date: 'Data:',
    advisorName: 'Nome do Assessor:',
  },
  header: {
    poweredBy: "Powered by DOMINION"
  },
  welcome: {
    title: "Bem-vindo ao Perfil de Risco",
    description: "Este questionário irá ajudá-lo a determinar o seu perfil de risco como investidor e fornecerá uma recomendação personalizada sobre como distribuir os seus investimentos.",
    whatWeEvaluate: "O que avaliaremos?",
    items: [
      "O seu horizonte temporal de investimento",
      "A sua experiência nos mercados financeiros",
      "A sua tolerância à volatilidade",
      "Os seus objetivos de investimento"
    ],
    duration: "O questionário leva aproximadamente 3-5 minutos para ser concluído.",
    keyboardHint: "Pressione {enter} para começar"
  },
  questions: [
    {
      id: 'q1',
      question: 'Qual é o seu horizonte temporal de investimento?',
      options: [
        { value: '10', label: '1-3 anos', description: 'Prazo médio-curto' },
        { value: '15', label: '3-5 anos', description: 'Prazo médio' },
        { value: '20', label: '5-10 anos', description: 'Prazo médio-longo' },
        { value: '25', label: 'Mais de 10 anos', description: 'Prazo longo, sem pressa' },
      ]
    },
    {
      id: 'q2',
      question: 'Qual é a sua experiência em investimentos?',
      options: [
        { value: '0', label: 'Nenhuma', description: 'Sou novo nisso' },
        { value: '5', label: 'Básica', description: 'Alguns investimentos simples' },
        { value: '10', label: 'Intermediária', description: 'Vários anos investindo' },
        { value: '15', label: 'Avançada', description: 'Ampla experiência em mercados' },
        { value: '20', label: 'Expert', description: 'Profissional ou muito experiente' },
      ]
    },
    {
      id: 'q3',
      question: 'Se o seu investimento caísse 20% em um mês, o que faria?',
      options: [
        { value: '0', label: 'Venderia tudo imediatamente', description: 'Não posso suportar as perdas' },
        { value: '5', label: 'Venderia a maior parte', description: 'Ficaria muito nervoso' },
        { value: '10', label: 'Esperaria para ver', description: 'Analisaria a situação' },
        { value: '15', label: 'Manteria minha posição', description: 'Confio na minha estratégia' },
        { value: '20', label: 'Compraria mais', description: 'É uma oportunidade' },
      ]
    },
    {
      id: 'q4',
      question: 'Que percentagem das suas poupanças destinará ao investimento?',
      options: [
        { value: '20', label: 'Menos de 10%', description: 'Apenas uma pequena parte' },
        { value: '15', label: '10-25%', description: 'Uma porção moderada' },
        { value: '10', label: '25-50%', description: 'Uma parte significativa' },
        { value: '5', label: '50-75%', description: 'A maior parte' },
        { value: '0', label: 'Mais de 75%', description: 'Quase todas as minhas poupanças' },
      ]
    },
    {
      id: 'q5',
      question: 'Qual é o seu objetivo principal de investimento?',
      options: [
        { value: '0', label: 'Preservar capital', description: 'Não quero perder dinheiro' },
        { value: '5', label: 'Gerar renda estável', description: 'Crescimento sustentado acima da inflação' },
        { value: '10', label: 'Crescimento moderado', description: 'Equilíbrio entre segurança e rentabilidade' },
        { value: '15', label: 'Crescimento significativo', description: 'Maximizar ganhos com risco controlado' },
        { value: '20', label: 'Máxima rentabilidade', description: 'Busco os maiores retornos possíveis' },
      ]
    },
    {
      id: 'q6',
      question: 'Como reage à volatilidade do mercado?',
      options: [
        { value: '0', label: 'Estressa-me muito', description: 'Perco o sono por isso' },
        { value: '5', label: 'Preocupa-me bastante', description: 'Verifico constantemente os meus investimentos' },
        { value: '10', label: 'Inquieta-me um pouco', description: 'Monitoro mas sem me obsessionar' },
        { value: '15', label: 'Aceito como normal', description: 'Faz parte do processo' },
        { value: '20', label: 'Vejo como oportunidade', description: 'Permite-me encontrar melhores preços' },
      ]
    },
  ],
  questionCard: {
    keyboardHint: "Pressione {numbers} para selecionar ou {enter} para continuar"
  },
  riskProfiles: {
    conservative: {
      profile: 'Conservador',
      description: 'Prioriza a segurança e estabilidade do seu capital. Prefere minimizar riscos mesmo que isso signifique retornos potenciais menores.',
      scoreRange: '0-20',
      ranges: [
        { category: 'Renda Fixa', min: 70, max: 100, color: '#5955FF' },
        { category: 'Renda Variável', min: 0, max: 30, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 10, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 0, color: '#FF6B6B' },
      ],
      funds: {
        'Renda Fixa': [
          { name: 'DCS Global Bonds', composition: '100% Renda Fixa' },
          { name: 'DCS Cash', composition: '100% Renda Fixa' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renda Fixa' },
        ],
        'Renda Variável': [
          { name: 'DCS Cautious', composition: '20% Renda Variável + 80% Renda Fixa' },
          { name: 'DCS Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
      }
    },
    moderateConservative: {
      profile: 'Moderado Conservador',
      description: 'Busca um equilíbrio inclinado para a segurança, mas está disposto a assumir algum risco para obter melhores retornos.',
      scoreRange: '21-40',
      ranges: [
        { category: 'Renda Fixa', min: 50, max: 70, color: '#5955FF' },
        { category: 'Renda Variável', min: 20, max: 50, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 5, color: '#FF6B6B' },
      ],
      funds: {
        'Renda Fixa': [
          { name: 'DCS Global Bonds', composition: '100% Renda Fixa' },
          { name: 'DCS Cash', composition: '100% Renda Fixa' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renda Fixa' },
        ],
        'Renda Variável': [
          { name: 'DCS Cautious', composition: '20% Renda Variável + 80% Renda Fixa' },
          { name: 'DCS Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renda Variável' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renda Variável' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderate: {
      profile: 'Moderado',
      description: 'Busca um equilíbrio balanceado entre risco e rentabilidade. Entende que a volatilidade faz parte de obter melhores retornos a longo prazo.',
      scoreRange: '41-60',
      ranges: [
        { category: 'Renda Fixa', min: 30, max: 50, color: '#5955FF' },
        { category: 'Renda Variável', min: 40, max: 80, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 10, color: '#FF6B6B' },
      ],
      funds: {
        'Renda Fixa': [
          { name: 'DCS Global Bonds', composition: '100% Renda Fixa' },
          { name: 'DCS Cash', composition: '100% Renda Fixa' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renda Fixa' },
        ],
        'Renda Variável': [
          { name: 'DCS Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
          { name: 'DCS Aggressive', composition: '80% Renda Variável + 20% Renda Fixa' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renda Variável + 40% Renda Fixa' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renda Variável' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renda Variável' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renda Variável' },
          { name: 'DCS New Technologies', composition: '100% Renda Variável' },
          { name: 'DCS Global Growth', composition: '100% Renda Variável' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderateAggressive: {
      profile: 'Moderado Agressivo',
      description: 'Está disposto a assumir riscos significativos em busca de maiores retornos. Tolera a volatilidade e tem um horizonte de investimento a longo prazo.',
      scoreRange: '61-80',
      ranges: [
        { category: 'Renda Fixa', min: 10, max: 30, color: '#5955FF' },
        { category: 'Renda Variável', min: 60, max: 90, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 15, color: '#FF6B6B' },
      ],
      funds: {
        'Renda Fixa': [
          { name: 'DCS Global Bonds', composition: '100% Renda Fixa' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renda Fixa' },
        ],
        'Renda Variável': [
          { name: 'DCS Aggressive', composition: '80% Renda Variável + 20% Renda Fixa' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renda Variável' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renda Variável' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renda Variável' },
          { name: 'DCS New Technologies', composition: '100% Renda Variável' },
          { name: 'DCS Global Growth', composition: '100% Renda Variável' },
          { name: 'DCS Managed Fund', composition: '100% Renda Variável' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Renda Variável' },
          { name: 'Nasdaq Tracker fund', composition: '100% Renda Variável' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    aggressive: {
      profile: 'Agressivo',
      description: 'Busca maximizar os seus retornos e está disposto a assumir altos níveis de risco. Tem experiência em investimentos e um horizonte temporal longo.',
      scoreRange: '81-120',
      ranges: [
        { category: 'Renda Fixa', min: 0, max: 10, color: '#5955FF' },
        { category: 'Renda Variável', min: 70, max: 100, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 20, color: '#FF6B6B' },
      ],
      funds: {
        'Renda Fixa': [
          { name: 'DCS Global Bonds', composition: '100% Renda Fixa' },
        ],
        'Renda Variável': [
          { name: 'DCS Aggressive', composition: '80% Renda Variável + 20% Renda Fixa' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renda Variável' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renda Variável' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renda Variável' },
          { name: 'DCS New Technologies', composition: '100% Renda Variável' },
          { name: 'DCS Global Growth', composition: '100% Renda Variável' },
          { name: 'DCS Managed Fund', composition: '100% Renda Variável' },
          { name: 'Opportunities Plus', composition: '100% Renda Variável' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Renda Variável' },
          { name: 'Nasdaq Tracker fund', composition: '100% Renda Variável' },
          { name: 'DCS Nifty Fifty Tracker Fund', composition: '100% Renda Variável' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    }
  },
  results: {
    title: "O seu perfil de risco:",
    chartTitle: "Distribuição Recomendada de Ativos",
    recommendationsTitle: "Faixas Recomendadas",
    fundsTitle: "Fundos Sugeridos",
    recommendationText: "da sua carteira",
    rangeText: "Faixa:",
    rangeLabel: "Faixa",
    totalAllocation: "Alocação Total do Portfolio",
    noteTitle: "Nota:",
    noteText: "Este perfil é orientativo. Recomendamos consultar um consultor financeiro antes de tomar decisões de investimento.",
    downloadPDF: "Baixar PDF",
    restartTest: "Refazer Teste",
    preparingPDF: "Preparando seu PDF...",
    pleaseWait: "Por favor aguarde um momento"
  },
  clientInfoModal: {
    title: "Informações do Cliente",
    clientNameLabel: "Nome Completo do Cliente",
    clientNamePlaceholder: "João Silva",
    advisorNameLabel: "Nome Completo do Consultor",
    advisorNamePlaceholder: "Maria Santos",
    cancelButton: "Cancelar",
    confirmButton: "Gerar PDF"
  },
  navigation: {
    start: "Começar",
    next: "Próximo",
    previous: "Anterior"
  },
  loading: {
    generatingResults: "Gerando seus resultados"
  },
  admin: {
    login: {
      title: "Painel de Administração",
      subtitle: "Gestão de Perfis de Risco",
      emailPlaceholder: "E-mail",
      passwordPlaceholder: "Senha",
      loginButton: "Entrar",
      invalidCredentials: "Usuário ou senha incorretos"
    },
    dashboard: {
      title: "Histórico de Perfis de Risco",
      subtitle: "Visualize e gerencie todos os perfis de risco gerados",
      logoutButton: "Sair",
      searchPlaceholder: "Buscar por nome de cliente ou assessor...",
      filterByProfile: "Filtrar por perfil",
      filterByLanguage: "Filtrar por idioma",
      allProfiles: "Todos os Perfis",
      allLanguages: "Todos os Idiomas",
      tableHeaders: {
        date: "Data",
        client: "Cliente",
        advisor: "Assessor",
        score: "Pontuação",
        profile: "Perfil",
        language: "Idioma",
        actions: "Ações"
      },
      actions: {
        view: "Ver",
        download: "Baixar",
        delete: "Excluir"
      },
      noResults: "Nenhum perfil de risco encontrado",
      noResultsDescription: "Tente ajustar sua busca ou filtros",
      totalResults: "Total: {count} perfis",
      confirmDelete: "Tem certeza de que deseja excluir este perfil de risco?",
      deleteSuccess: "Perfil de risco excluído com sucesso",
      deleteCancel: "Cancelar"
    }
  }
};